/* Geometry Tools — Ruler, Protractor, Compass as draggable/rotatable overlay SVG groups.
 *
 * These are non-persistent OVERLAY props (not scene elements). Position + rotation kept
 * in local state and rendered at the top of the SVG stack.
 * The compass CAN draw an arc that gets committed as a set of stroke points.
 */
import React, { useEffect, useState } from "react";

// ─────────────── Ruler ───────────────
export function GeometryRuler({ state, setState, containerRef, viewRef, onClose }) {
  const [drag, setDrag] = useState(null);
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const svg = containerRef.current?.querySelector('[data-testid="wb-svg"]');
      if (!svg) return;
      const r = svg.getBoundingClientRect();
      const cx = e.clientX ?? e.touches?.[0]?.clientX;
      const cy = e.clientY ?? e.touches?.[0]?.clientY;
      const nx = (cx - r.left) / r.width;
      const ny = (cy - r.top) / r.height;
      const v = viewRef.current;
      const wx = v.panX + nx * (1600 / v.zoom);
      const wy = v.panY + ny * (1000 / v.zoom);
      if (drag.kind === "move") setState((s) => ({ ...s, x: wx - drag.dx, y: wy - drag.dy }));
      else if (drag.kind === "rotate") {
        const dx = wx - state.x; const dy = wy - state.y;
        setState((s) => ({ ...s, rotation: Math.atan2(dy, dx) * 180 / Math.PI }));
      }
    };
    const onUp = () => setDrag(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [drag, containerRef, viewRef, setState, state.x, state.y]);

  const worldPt = (e) => {
    const svg = containerRef.current?.querySelector('[data-testid="wb-svg"]');
    const r = svg.getBoundingClientRect();
    const cx = e.clientX ?? e.touches?.[0]?.clientX;
    const cy = e.clientY ?? e.touches?.[0]?.clientY;
    const nx = (cx - r.left) / r.width;
    const ny = (cy - r.top) / r.height;
    const v = viewRef.current;
    return { x: v.panX + nx * (1600 / v.zoom), y: v.panY + ny * (1000 / v.zoom) };
  };

  const length = 600;
  const height = 60;
  const ticks = [];
  for (let i = 0; i <= 30; i++) {
    const long = i % 5 === 0;
    ticks.push(
      <line key={`t${i}`} x1={i * 20} y1={0} x2={i * 20} y2={long ? 22 : 12}
        stroke="#0A0C10" strokeWidth={long ? 1.5 : 1} />
    );
    if (long) ticks.push(
      <text key={`n${i}`} x={i * 20} y={40} fontSize={11} textAnchor="middle" fill="#0A0C10">{i}</text>
    );
  }

  return (
    <g transform={`translate(${state.x}, ${state.y}) rotate(${state.rotation || 0})`} data-testid="geo-ruler">
      <rect x={0} y={0} width={length} height={height} rx={4} fill="rgba(255,214,0,0.75)" stroke="#0A0C10" strokeWidth={1.5} />
      <g>{ticks}</g>
      {/* Move handle */}
      <g onMouseDown={(e) => { e.stopPropagation(); const p = worldPt(e); setDrag({ kind: "move", dx: p.x - state.x, dy: p.y - state.y }); }}
         style={{ cursor: "move" }} data-testid="geo-ruler-move">
        <rect x={length - 60} y={height - 20} width={20} height={16} fill="rgba(10,12,16,0.85)" />
      </g>
      {/* Rotate handle */}
      <g onMouseDown={(e) => { e.stopPropagation(); setDrag({ kind: "rotate" }); }} style={{ cursor: "crosshair" }} data-testid="geo-ruler-rotate">
        <circle cx={length - 20} cy={height / 2} r={10} fill="#0A0C10" />
        <text x={length - 20} y={height / 2 + 4} fontSize={10} textAnchor="middle" fill="#FFD600">↻</text>
      </g>
      {/* Close handle */}
      <g onMouseDown={(e) => { e.stopPropagation(); onClose(); }} style={{ cursor: "pointer" }} data-testid="geo-ruler-close">
        <circle cx={12} cy={height / 2} r={10} fill="#EF4444" />
        <text x={12} y={height / 2 + 4} fontSize={12} textAnchor="middle" fill="#fff">×</text>
      </g>
    </g>
  );
}

// ─────────────── Protractor ───────────────
export function GeometryProtractor({ state, setState, containerRef, viewRef, onClose }) {
  const [drag, setDrag] = useState(null);
  const radius = 220;
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const svg = containerRef.current?.querySelector('[data-testid="wb-svg"]');
      if (!svg) return;
      const r = svg.getBoundingClientRect();
      const cx = e.clientX ?? e.touches?.[0]?.clientX;
      const cy = e.clientY ?? e.touches?.[0]?.clientY;
      const nx = (cx - r.left) / r.width;
      const ny = (cy - r.top) / r.height;
      const v = viewRef.current;
      const wx = v.panX + nx * (1600 / v.zoom);
      const wy = v.panY + ny * (1000 / v.zoom);
      if (drag.kind === "move") setState((s) => ({ ...s, x: wx - drag.dx, y: wy - drag.dy }));
      else if (drag.kind === "rotate") {
        const dx = wx - state.x; const dy = wy - state.y;
        setState((s) => ({ ...s, rotation: Math.atan2(dy, dx) * 180 / Math.PI }));
      }
    };
    const onUp = () => setDrag(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [drag, containerRef, viewRef, setState, state.x, state.y]);

  const worldPt = (e) => {
    const svg = containerRef.current?.querySelector('[data-testid="wb-svg"]');
    const r = svg.getBoundingClientRect();
    const cx = e.clientX ?? e.touches?.[0]?.clientX;
    const cy = e.clientY ?? e.touches?.[0]?.clientY;
    const nx = (cx - r.left) / r.width;
    const ny = (cy - r.top) / r.height;
    const v = viewRef.current;
    return { x: v.panX + nx * (1600 / v.zoom), y: v.panY + ny * (1000 / v.zoom) };
  };

  const marks = [];
  for (let deg = 0; deg <= 180; deg += 5) {
    const r = deg % 10 === 0 ? radius : radius - 10;
    const inner = deg % 10 === 0 ? radius - 20 : radius - 14;
    const rad = (deg * Math.PI) / 180;
    const x1 = -Math.cos(rad) * inner;
    const y1 = -Math.sin(rad) * inner;
    const x2 = -Math.cos(rad) * r;
    const y2 = -Math.sin(rad) * r;
    marks.push(<line key={`m${deg}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#0A0C10" strokeWidth={deg % 30 === 0 ? 1.6 : 0.9} />);
    if (deg % 30 === 0) {
      const lx = -Math.cos(rad) * (radius - 34);
      const ly = -Math.sin(rad) * (radius - 34);
      marks.push(<text key={`l${deg}`} x={lx} y={ly + 4} fontSize={12} textAnchor="middle" fill="#0A0C10">{deg}</text>);
    }
  }

  return (
    <g transform={`translate(${state.x}, ${state.y}) rotate(${state.rotation || 0})`} data-testid="geo-protractor">
      <path d={`M -${radius} 0 A ${radius} ${radius} 0 0 1 ${radius} 0 L 0 0 Z`}
        fill="rgba(139,92,246,0.18)" stroke="#7C3AED" strokeWidth={1.5} />
      <g>{marks}</g>
      <line x1={-radius + 8} y1={0} x2={radius - 8} y2={0} stroke="#0A0C10" strokeWidth={1} />
      <circle cx={0} cy={0} r={4} fill="#0A0C10" />
      {/* Move (drag body) */}
      <circle cx={0} cy={-radius / 2} r={12} fill="rgba(10,12,16,0.5)"
        onMouseDown={(e) => { e.stopPropagation(); const p = worldPt(e); setDrag({ kind: "move", dx: p.x - state.x, dy: p.y - state.y }); }}
        style={{ cursor: "move" }} data-testid="geo-protractor-move" />
      {/* Rotate */}
      <g onMouseDown={(e) => { e.stopPropagation(); setDrag({ kind: "rotate" }); }} style={{ cursor: "crosshair" }} data-testid="geo-protractor-rotate">
        <circle cx={radius - 20} cy={-20} r={12} fill="#7C3AED" />
        <text x={radius - 20} y={-16} fontSize={11} textAnchor="middle" fill="#fff">↻</text>
      </g>
      {/* Close */}
      <g onMouseDown={(e) => { e.stopPropagation(); onClose(); }} style={{ cursor: "pointer" }} data-testid="geo-protractor-close">
        <circle cx={-radius + 20} cy={-20} r={12} fill="#EF4444" />
        <text x={-radius + 20} y={-16} fontSize={12} textAnchor="middle" fill="#fff">×</text>
      </g>
    </g>
  );
}

// ─────────────── Compass ───────────────
/* Compass: pivot at (state.x, state.y), pencil end at radius `r` and angle `a`.
 * User drags the pencil end to change radius/angle. Clicking "draw" commits
 * an arc as a series of stroke points.
 */
export function GeometryCompass({ state, setState, containerRef, viewRef, onClose, onDrawArc }) {
  const [drag, setDrag] = useState(null); // "move" (pivot) | "pencil" | null
  const [drawing, setDrawing] = useState(false);
  const [arcPts, setArcPts] = useState([]);
  const [pivotStart, setPivotStart] = useState(null);

  const worldPt = (e) => {
    const svg = containerRef.current?.querySelector('[data-testid="wb-svg"]');
    const r = svg.getBoundingClientRect();
    const cx = e.clientX ?? e.touches?.[0]?.clientX;
    const cy = e.clientY ?? e.touches?.[0]?.clientY;
    const nx = (cx - r.left) / r.width;
    const ny = (cy - r.top) / r.height;
    const v = viewRef.current;
    return { x: v.panX + nx * (1600 / v.zoom), y: v.panY + ny * (1000 / v.zoom) };
  };

  useEffect(() => {
    if (!drag && !drawing) return;
    const onMove = (e) => {
      const p = worldPt(e);
      if (drag?.kind === "move") setState((s) => ({ ...s, x: p.x - drag.dx, y: p.y - drag.dy }));
      else if (drag?.kind === "pencil") {
        const dx = p.x - state.x; const dy = p.y - state.y;
        const r = Math.sqrt(dx * dx + dy * dy);
        const a = Math.atan2(dy, dx);
        setState((s) => ({ ...s, r, a }));
      } else if (drawing) {
        const dx = p.x - state.x; const dy = p.y - state.y;
        const a = Math.atan2(dy, dx);
        setState((s) => ({ ...s, a }));
        setArcPts((pts) => [...pts, { x: state.x + Math.cos(a) * state.r, y: state.y + Math.sin(a) * state.r }]);
      }
    };
    const onUp = () => {
      if (drawing && arcPts.length > 1 && onDrawArc) {
        onDrawArc(arcPts);
      }
      setArcPts([]);
      setDrawing(false);
      setDrag(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [drag, drawing, arcPts, onDrawArc, setState, state.x, state.y, state.r]);

  const px = state.x + Math.cos(state.a || 0) * (state.r || 120);
  const py = state.y + Math.sin(state.a || 0) * (state.r || 120);

  return (
    <g data-testid="geo-compass">
      {/* Arc preview while drawing */}
      {arcPts.length > 1 && (
        <path d={arcPts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ")}
          fill="none" stroke="#DC2626" strokeWidth={2} strokeLinecap="round" strokeDasharray="4 4" />
      )}
      {/* Radius circle preview */}
      <circle cx={state.x} cy={state.y} r={state.r || 120} fill="none" stroke="rgba(220,38,38,0.35)" strokeWidth={1} strokeDasharray="6 6" />
      {/* Compass legs */}
      <line x1={state.x} y1={state.y} x2={px} y2={py} stroke="#0A0C10" strokeWidth={4} />
      {/* Pivot */}
      <circle cx={state.x} cy={state.y} r={12} fill="#0A0C10"
        onMouseDown={(e) => { e.stopPropagation(); const p = worldPt(e); setDrag({ kind: "move", dx: p.x - state.x, dy: p.y - state.y }); }}
        style={{ cursor: "move" }} data-testid="geo-compass-pivot" />
      <circle cx={state.x} cy={state.y} r={4} fill="#FFD600" pointerEvents="none" />
      {/* Pencil end */}
      <circle cx={px} cy={py} r={14} fill="#DC2626"
        onMouseDown={(e) => { e.stopPropagation(); setDrag({ kind: "pencil" }); }}
        style={{ cursor: "grab" }} data-testid="geo-compass-pencil" />
      <text x={px} y={py + 4} fontSize={11} textAnchor="middle" fill="#fff" pointerEvents="none">✎</text>
      {/* Draw button */}
      <g onMouseDown={(e) => { e.stopPropagation(); setDrawing(true); setArcPts([{ x: px, y: py }]); }}
         style={{ cursor: "crosshair" }} data-testid="geo-compass-draw">
        <rect x={state.x - 30} y={state.y - 60} width={60} height={22} rx={4} fill="#0A0C10" />
        <text x={state.x} y={state.y - 45} fontSize={12} textAnchor="middle" fill="#FFD600" fontWeight="bold">DRAW</text>
      </g>
      {/* Close */}
      <g onMouseDown={(e) => { e.stopPropagation(); onClose(); }} style={{ cursor: "pointer" }} data-testid="geo-compass-close">
        <circle cx={state.x + 40} cy={state.y - 50} r={11} fill="#EF4444" />
        <text x={state.x + 40} y={state.y - 46} fontSize={12} textAnchor="middle" fill="#fff">×</text>
      </g>
    </g>
  );
}
